import type { CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';
import type { MeetingPrivate, MeetingPublic } from '@repo/types/meetings';
import type { MeetingAttendance } from '@repo/types/meetings';
import type { MemberChanges } from '@repo/types/memberChanges';
import type { DatetimeCursorPagination, PaginationWithSort } from '@repo/types/pagination';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class MeetingService {
  static async getMeeting(id?: number): Promise<AxiosResponse<MeetingPrivate>> {
    return await axiosInstance.get(`/api/meeting/${id}`);
  }

  static async getAssignedMeetings(params: GetMeetingsParams): Promise<AxiosResponse<Array<MeetingPublic>>> {
    return await axiosInstance.get('/api/meeting/assigned', {
      params: params,
    });
  }

  static async postMeeting(data: PostMeetingData): Promise<AxiosResponse<MeetingPrivate>> {
    return await axiosInstance.post('/api/meeting', data);
  }

  static async editMeeting(data: EditMeetingData): Promise<AxiosResponse<MeetingPrivate>> {
    const { id, ...rest } = data;
    return await axiosInstance.put(`/api/meeting/${id}`, rest);
  }

  static async deleteMeeting(id: number): Promise<AxiosResponse<number>> {
    return await axiosInstance.delete(`/api/meeting/${id}`);
  }

  static async editAttendance(data: EditAttendanceData): Promise<AxiosResponse<void>> {
    const { id, attendanceData } = data;
    return await axiosInstance.put(`/api/meeting/${id}/attendance`, {
      userIds: attendanceData,
    });
  }
}

export interface PostMeetingData extends Omit<CalendarUpsertSchema, 'users' | 'groups' | 'location'> {
  userArr: number[];
  groupArr: number[];
  locationId: number | null;
}

export interface EditMeetingData extends Partial<PostMeetingData> {
  id: number;
  isCancelled?: boolean;
  memberChanges?: MemberChanges[];
}

export interface GetMeetingPrefetchProps {
  id: number;
  initialData: Partial<MeetingPublic>;
}

export interface GetMeetingsParams extends Partial<DatetimeCursorPagination>, Partial<PaginationWithSort> {
  id?: number;
}

export interface EditAttendanceData {
  id: number;
  attendanceData: MeetingAttendance[];
}
